import { Injectable, OnDestroy } from "@angular/core";

@Injectable({ providedIn: "root" })
export class GlobalLinkHandoffService implements OnDestroy {
  private cleanup: Array<() => void> = [];
  private TTL_MS = 20_000; // accept within 20s

  // NEW: cache one token per element and per canonical href (href without ?handoff)
  private elemToken = new WeakMap<HTMLAnchorElement, string>();
  private hrefToken = new Map<string, string>();

  init() {
    this.sweepOldHandoffs();

    this.on("pointerdown", true, this.onPointerDown);
    this.on("mousedown", true, this.onMouseDown);
    this.on("auxclick", true, this.onAuxClick);
    this.on("click", true, this.onClick);
    this.on("contextmenu", true, this.onContextMenu);

    // UPDATED: pointerenter no longer creates a token—only re-applies if one exists
    this.on("pointerenter", true, this.onPointerEnter);
  }

  ngOnDestroy() {
    this.cleanup.forEach((fn) => fn());
  }

  private on<K extends keyof DocumentEventMap>(type: K, capture: boolean, handler: (e: DocumentEventMap[K]) => void) {
    const bound = handler.bind(this) as EventListener;
    document.addEventListener(type, bound, { capture, passive: true });
    this.cleanup.push(() => document.removeEventListener(type, bound, { capture } as any));
  }

  // ===== Event handlers =====
  private onPointerDown(e: PointerEvent) {
    const a = this.anchorFromEvent(e);
    if (!a) return;
    const intendsNewTab = e.button === 1 || e.button === 2 || a.target === "_blank" || e.ctrlKey || e.metaKey || e.shiftKey;
    if (intendsNewTab) this.prepareHandoff(a, /*write*/ true, /*create*/ true);
  }

  private onMouseDown(e: MouseEvent) {
    if (e.button !== 2) return; // right-click
    const a = this.anchorFromEvent(e);
    if (a) this.prepareHandoff(a, /*write*/ true, /*create*/ true);
  }

  private onAuxClick(e: MouseEvent) {
    if (e.button !== 1) return; // middle-click
    const a = this.anchorFromEvent(e);
    if (a) this.prepareHandoff(a, /*write*/ true, /*create*/ true);
  }

  private onClick(e: MouseEvent) {
    const a = this.anchorFromEvent(e);
    if (!a) return;
    if (a.target === "_blank" || e.ctrlKey || e.metaKey || e.shiftKey) {
      this.prepareHandoff(a, /*write*/ true, /*create*/ true);
    }
  }

  private onContextMenu(e: MouseEvent) {
    const a = this.anchorFromEvent(e);
    if (a) this.prepareHandoff(a, /*write*/ true, /*create*/ true);
  }

  private onPointerEnter(e: PointerEvent) {
    const a = this.anchorFromEvent(e);
    if (a) this.prepareHandoff(a, /*write*/ false, /*create*/ false);
  }

  // ===== Core helpers =====
  private anchorFromEvent(ev: Event): HTMLAnchorElement | null {
    // Prefer composedPath (handles Shadow DOM nicely)
    const path = typeof (ev as any).composedPath === "function" ? ((ev as any).composedPath() as Array<EventTarget>) : [];

    for (const node of path) {
      if (node && node instanceof HTMLAnchorElement && node.hasAttribute("href")) {
        if (this.eligible(node)) return node;
      }
      if (node && node instanceof Element) {
        const a = node.closest<HTMLAnchorElement>("a[href]");
        if (a && this.eligible(a)) return a;
      }
    }

    // Fallback if composedPath is missing or didn’t include an <a>
    const tgt = ev.target;
    // Convert EventTarget -> Element (or its parent Element if it’s a Text node)
    let el: Element | null = tgt instanceof Element ? tgt : tgt instanceof Node ? tgt.parentElement : null;

    while (el) {
      if (el instanceof HTMLAnchorElement && el.hasAttribute("href")) {
        return this.eligible(el) ? el : null;
      }
      el = el.parentElement;
    }

    return null;
  }

  private eligible(a: HTMLAnchorElement): boolean {
    if (a.dataset["noHandoff"] === "true") return false;
    if (a.hasAttribute("download")) return false;
    const raw = a.getAttribute("href");
    if (!raw) return false;
    try {
      const url = new URL(raw, window.location.href);
      if (url.protocol === "mailto:" || url.protocol === "tel:") return false;
      if (url.origin !== window.location.origin) return false; // same-origin only
      return true;
    } catch {
      return false;
    }
  }

  // UPDATED: idempotent per element AND per canonical href; can skip creation/writing
  private prepareHandoff(a: HTMLAnchorElement, write: boolean, create: boolean) {
    try {
      const raw = a.getAttribute("href");
      if (!raw) return;
      const url = new URL(raw, window.location.href);

      const existingInUrl = url.searchParams.get("handoff");
      const canonical = this.canonicalFromUrl(url); // href without ?handoff

      let token = existingInUrl || a.dataset["handoffToken"] || this.elemToken.get(a) || this.hrefToken.get(canonical) || null;

      if (!token && !create) {
        // pointerenter: do not create a new token—just bail
        return;
      }

      if (!token) {
        token = this.newToken();
      }

      // cache on element + href so subsequent events (or re-renders) reuse it
      a.dataset["handoffToken"] = token;
      this.elemToken.set(a, token);
      this.hrefToken.set(canonical, token);

      if (write) this.writeHandoffOnce(token);

      // ensure href contains the SAME token
      this.applyTokenToHref(a, token);
      queueMicrotask(() => this.applyTokenToHref(a, token));
      setTimeout(() => this.applyTokenToHref(a, token), 0);

      if (a.target === "_blank" && !/\bnoopener\b/i.test(a.rel)) {
        a.rel = (a.rel ? a.rel + " " : "") + "noopener";
      }
    } catch {
      /* no-op */
    }
  }

  private applyTokenToHref(a: HTMLAnchorElement, token: string) {
    const raw = a.getAttribute("href");
    if (!raw) return;
    try {
      const url = new URL(raw, window.location.href);

      // Put token in query
      url.searchParams.set("handoff", token);

      // Also put token in hash so it survives client-side redirects that drop query params
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
      hashParams.set("handoff", token);
      const newHash = hashParams.toString();
      url.hash = newHash ? "#" + newHash : "";

      a.href = url.toString();
    } catch {
      /* ignore */
    }
  }

  // NEW: canonical key for href-token map (strip only the handoff param)
  private canonicalFromUrl(url: URL): string {
    const u = new URL(url.toString());
    u.searchParams.delete("handoff");
    return `${u.origin}${u.pathname}${u.search ? "?" + u.searchParams.toString() : ""}`;
  }

  private newToken(): string {
    const arr = new Uint32Array(4);
    crypto.getRandomValues(arr);
    return Array.from(arr).join("-");
  }

  // UPDATED: write once per token
  private writeHandoffOnce(token: string) {
    const key = `handoff:${token}`;
    try {
      if (localStorage.getItem(key)) return;
      const payload: Record<string, string> = {};
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i)!;
        const v = sessionStorage.getItem(k);
        if (v != null) payload[k] = v;
      }
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), payload }));
    } catch {
      this.sweepOldHandoffs();
      try {
        if (!localStorage.getItem(key)) {
          const payload: Record<string, string> = {};
          for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i)!;
            const v = sessionStorage.getItem(k);
            if (v != null) payload[k] = v;
          }
          localStorage.setItem(key, JSON.stringify({ ts: Date.now(), payload }));
        }
      } catch {}
    }
  }

  private sweepOldHandoffs() {
    try {
      const now = Date.now();
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)!;
        if (!key.startsWith("handoff:")) continue;
        const raw = localStorage.getItem(key);
        if (!raw) {
          localStorage.removeItem(key);
          continue;
        }
        try {
          const { ts } = JSON.parse(raw) as { ts: number };
          if (!ts || now - ts > this.TTL_MS) localStorage.removeItem(key);
        } catch {
          localStorage.removeItem(key);
        }
      }
    } catch {
      /* ignore */
    }
  }
}
